/**
 * Automation Dashboard - Visual Workflow Builder
 * Comprehensive automation rules management with visual workflow editor
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Zap, Plus, Play, Pause, Trash2, Edit, Copy, Search,
  Filter, TrendingUp, CheckCircle, XCircle, Clock,
  ArrowRight, Settings, Eye, Activity, BarChart3, GitBranch,
  AlertCircle, X, Tag as TagIcon
} from 'lucide-react';
import './AutomationDashboard.css';

function AutomationDashboard({ apiWithAccessControl }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ triggers: [], actions: [], conditions: [] });
  const [selectedRule, setSelectedRule] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    triggerType: '',
    isActive: ''
  });
  const [executionFilters, setExecutionFilters] = useState({
    ruleId: '',
    status: '',
    merchantId: ''
  });

  // Status message and confirmation
  const [statusMessage, setStatusMessage] = useState(null);
  const [confirmDeleteRuleId, setConfirmDeleteRuleId] = useState(null);
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const statusTimerRef = useRef(null);

  // Tag and segment options for auto-fetch dropdowns
  const [tagOptions, setTagOptions] = useState([]);
  const [segmentOptions, setSegmentOptions] = useState([]);

  const showStatus = useCallback((type, text) => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setStatusMessage({ type, text });
    statusTimerRef.current = setTimeout(() => setStatusMessage(null), 4000);
  }, []);

  // Load data on mount
  useEffect(() => {
    loadOptions();
    fetchTagsAndSegments();
    if (activeTab === 'overview') {
      loadStats();
    } else if (activeTab === 'rules') {
      loadRules();
    } else if (activeTab === 'executions') {
      loadExecutions();
    }
    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    };
  }, [activeTab]);

  const fetchTagsAndSegments = async () => {
    try {
      const [tagsRes, segmentsRes] = await Promise.all([
        apiWithAccessControl('/admin/tags?type=user'),
        apiWithAccessControl('/admin/segments')
      ]);
      const tagsData = await tagsRes.json();
      const segmentsData = await segmentsRes.json();
      if (tagsData && tagsData.tags) {
        setTagOptions(tagsData.tags.map(t => ({
          value: t.tag || t.name,
          label: t.tag || t.name,
          count: t.count || 0
        })));
      }
      if (segmentsData && segmentsData.segments) {
        setSegmentOptions(segmentsData.segments.map(s => ({
          value: s.id,
          label: s.name,
          type: s.type,
          recipientCount: s.recipientCount || 0
        })));
      }
    } catch (error) {
      console.error('Error fetching tags/segments:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'rules') {
      loadRules();
    }
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'executions') {
      loadExecutions();
    }
  }, [executionFilters]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await apiWithAccessControl('/admin/automation/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.triggerType) params.append('triggerType', filters.triggerType);
      if (filters.isActive) params.append('isActive', filters.isActive);

      const response = await apiWithAccessControl(`/admin/automation/rules?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setRules(data.data);
      }
    } catch (error) {
      console.error('Error loading rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExecutions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (executionFilters.ruleId) params.append('ruleId', executionFilters.ruleId);
      if (executionFilters.status) params.append('status', executionFilters.status);
      if (executionFilters.merchantId) params.append('merchantId', executionFilters.merchantId);

      const response = await apiWithAccessControl(`/admin/automation/executions?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setExecutions(data.data);
      }
    } catch (error) {
      console.error('Error loading executions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const response = await apiWithAccessControl('/admin/automation/options');
      const data = await response.json();
      if (data.success) {
        setOptions(data.data);
      }
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const toggleRule = async (ruleId) => {
    try {
      const response = await apiWithAccessControl(`/admin/automation/rules/${ruleId}/toggle`, {
        method: 'PATCH'
      });
      const data = await response.json();
      if (data.success) {
        loadRules();
        showStatus('success', data.message || 'Rule toggled successfully');
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
      showStatus('error', 'Failed to toggle rule');
    }
  };

  const deleteRule = async (ruleId) => {
    setDeletingRuleId(ruleId);
    try {
      const response = await apiWithAccessControl(`/admin/automation/rules/${ruleId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        loadRules();
        showStatus('success', 'Rule deleted successfully');
        setConfirmDeleteRuleId(null);
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      showStatus('error', 'Failed to delete rule');
    } finally {
      setDeletingRuleId(null);
    }
  };

  const createRule = () => {
    setSelectedRule(null);
    setShowBuilder(true);
  };

  const editRule = (rule) => {
    setSelectedRule(rule);
    setShowBuilder(true);
  };

  // Overview Dashboard
  const OverviewDashboard = () => (
    <div className="mkt-auto-overview">
      <div className="mkt-auto-header">
        <div>
          <h1>Automation Rules & Workflows</h1>
          <p>Trigger-based automation for merchant engagement</p>
        </div>
        <button className="mkt-auto-btn-primary" onClick={createRule}>
          <Plus size={18} />
          Create Rule
        </button>
      </div>

      {loading && <div className="mkt-auto-loading">Loading statistics...</div>}

      {stats && (
        <>
          {/* Quick Stats */}
          <div className="mkt-auto-stats-grid">
            <div className="mkt-auto-stat-card">
              <div className="mkt-auto-stat-icon" style={{ background: '#e8f5f3', color: '#155a51' }}>
                <Zap size={32} />
              </div>
              <div className="mkt-auto-stat-content">
                <h3>{stats.totalRules}</h3>
                <p>Total Rules</p>
                <small>{stats.activeRules} active</small>
              </div>
            </div>

            <div className="mkt-auto-stat-card">
              <div className="mkt-auto-stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                <Activity size={32} />
              </div>
              <div className="mkt-auto-stat-content">
                <h3>{stats.totalExecutions}</h3>
                <p>Total Executions</p>
                <small>{stats.recentExecutions} in last 7 days</small>
              </div>
            </div>

            <div className="mkt-auto-stat-card">
              <div className="mkt-auto-stat-icon" style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                <CheckCircle size={32} />
              </div>
              <div className="mkt-auto-stat-content">
                <h3>{stats.successRate}</h3>
                <p>Success Rate</p>
              </div>
            </div>

            <div className="mkt-auto-stat-card">
              <div className="mkt-auto-stat-icon" style={{ background: '#fff3e0', color: '#f57c00' }}>
                <TrendingUp size={32} />
              </div>
              <div className="mkt-auto-stat-content">
                <h3>{stats.inactiveRules}</h3>
                <p>Inactive Rules</p>
              </div>
            </div>
          </div>

          {/* Top Performing Rules */}
          {stats.topRules && stats.topRules.length > 0 && (
            <div className="mkt-auto-top-rules">
              <h2>Top Performing Rules</h2>
              <div className="mkt-auto-top-rules-list">
                {stats.topRules.map((rule) => (
                  <div key={rule.id} className="mkt-auto-top-rule-card">
                    <div className="mkt-auto-rule-info">
                      <h3>{rule.name}</h3>
                      <span className="mkt-auto-trigger-badge">{rule.triggerType.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="mkt-auto-rule-stats">
                      <div className="mkt-auto-stat-item">
                        <strong>{rule.totalExecutions}</strong>
                        <span>Executions</span>
                      </div>
                      <div className="mkt-auto-stat-item">
                        <strong>{rule.successfulExecutions}</strong>
                        <span>Successful</span>
                      </div>
                      <div className="mkt-auto-stat-item">
                        <strong>
                          {rule.totalExecutions > 0
                            ? ((rule.successfulExecutions / rule.totalExecutions) * 100).toFixed(0)
                            : 0}%
                        </strong>
                        <span>Success Rate</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mkt-auto-quick-actions">
            <h2>Quick Actions</h2>
            <div className="mkt-auto-actions-grid">
              <button className="mkt-auto-action-card" onClick={createRule}>
                <Plus size={24} />
                <h3>Create New Rule</h3>
                <p>Build custom automation workflow</p>
              </button>

              <button className="mkt-auto-action-card" onClick={() => setActiveTab('rules')}>
                <Eye size={24} />
                <h3>View All Rules</h3>
                <p>Manage existing automation rules</p>
              </button>

              <button className="mkt-auto-action-card" onClick={() => setActiveTab('templates')}>
                <Copy size={24} />
                <h3>Use Template</h3>
                <p>Start from pre-built templates</p>
              </button>

              <button className="mkt-auto-action-card" onClick={() => setActiveTab('executions')}>
                <BarChart3 size={24} />
                <h3>View History</h3>
                <p>Execution logs and analytics</p>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Execution History View
  const ExecutionHistoryView = () => {
    const getStatusBadgeClass = (status) => {
      switch (status) {
        case 'SUCCESS': return 'mkt-auto-status-badge active';
        case 'FAILED': return 'mkt-auto-status-badge inactive';
        case 'PENDING': return 'mkt-auto-status-badge' ;
        default: return 'mkt-auto-status-badge';
      }
    };

    const formatExecutionTime = (duration) => {
      if (!duration) return 'N/A';
      if (duration < 1000) return `${duration}ms`;
      return `${(duration / 1000).toFixed(2)}s`;
    };

    return (
      <div className="mkt-auto-rules-view">
        <div className="mkt-auto-view-header">
          <div>
            <h1>Execution History</h1>
            <p>View detailed logs of automation rule executions</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mkt-auto-filters">
          <select
            value={executionFilters.ruleId}
            onChange={(e) => setExecutionFilters({ ...executionFilters, ruleId: e.target.value })}
          >
            <option value="">All Rules</option>
            {rules.map((rule) => (
              <option key={rule.id} value={rule.id}>
                {rule.name}
              </option>
            ))}
          </select>

          <select
            value={executionFilters.status}
            onChange={(e) => setExecutionFilters({ ...executionFilters, status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILED">Failed</option>
            <option value="PENDING">Pending</option>
          </select>

          <input
            type="text"
            placeholder="Merchant ID..."
            value={executionFilters.merchantId}
            onChange={(e) => setExecutionFilters({ ...executionFilters, merchantId: e.target.value })}
            style={{ padding: '10px 16px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>

        {/* Executions List */}
        {loading ? (
          <div className="mkt-auto-loading">Loading executions...</div>
        ) : executions.length === 0 ? (
          <div className="mkt-auto-empty">
            <Activity size={64} />
            <h3>No executions yet</h3>
            <p>Automation rule executions will appear here</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {executions.map((execution) => (
              <div key={execution.id} className="mkt-auto-rule-card">
                <div className="mkt-auto-rule-card-header">
                  <div className="mkt-auto-rule-title">
                    <h3>{execution.rule?.name || 'Unknown Rule'}</h3>
                    <span className={getStatusBadgeClass(execution.status)}>
                      {execution.status}
                    </span>
                  </div>
                  <button
                    className="mkt-auto-btn-icon"
                    onClick={() => setSelectedExecution(execution)}
                    title="View Details"
                  >
                    <Eye size={16} />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
                  <div>
                    <small style={{ color: '#666', display: 'block', marginBottom: '4px' }}>Merchant ID</small>
                    <strong>{execution.merchantId}</strong>
                  </div>
                  <div>
                    <small style={{ color: '#666', display: 'block', marginBottom: '4px' }}>Executed At</small>
                    <strong>{new Date(execution.executedAt).toLocaleString()}</strong>
                  </div>
                  <div>
                    <small style={{ color: '#666', display: 'block', marginBottom: '4px' }}>Duration</small>
                    <strong>{formatExecutionTime(execution.executionDuration)}</strong>
                  </div>
                  {execution.completedAt && (
                    <div>
                      <small style={{ color: '#666', display: 'block', marginBottom: '4px' }}>Completed At</small>
                      <strong>{new Date(execution.completedAt).toLocaleString()}</strong>
                    </div>
                  )}
                </div>

                {execution.errorMessage && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '13px' }}>
                    <strong>Error:</strong> {execution.errorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Rules List View
  const RulesListView = () => (
    <div className="mkt-auto-rules-view">
      <div className="mkt-auto-view-header">
        <div>
          <h1>Automation Rules</h1>
          <p>Manage and monitor your automation workflows</p>
        </div>
        <button className="mkt-auto-btn-primary" onClick={createRule}>
          <Plus size={18} />
          Create Rule
        </button>
      </div>

      {/* Filters */}
      <div className="mkt-auto-filters">
        <div className="mkt-auto-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search rules..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        <select
          value={filters.triggerType}
          onChange={(e) => setFilters({ ...filters, triggerType: e.target.value })}
        >
          <option value="">All Triggers</option>
          {options.triggers.map((trigger) => (
            <option key={trigger.value} value={trigger.value}>
              {trigger.label}
            </option>
          ))}
        </select>

        <select
          value={filters.isActive}
          onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Rules Grid */}
      {loading ? (
        <div className="mkt-auto-loading">Loading rules...</div>
      ) : rules.length === 0 ? (
        <div className="mkt-auto-empty">
          <Zap size={64} />
          <h3>No automation rules yet</h3>
          <p>Create your first automation rule to get started</p>
          <button className="mkt-auto-btn-primary" onClick={createRule}>
            <Plus size={18} />
            Create Rule
          </button>
        </div>
      ) : (
        <div className="mkt-auto-rules-grid">
          {rules.map((rule) => (
            <div key={rule.id} className="mkt-auto-rule-card">
              <div className="mkt-auto-rule-card-header">
                <div className="mkt-auto-rule-title">
                  <h3>{rule.name}</h3>
                  <span className={`mkt-auto-status-badge ${rule.isActive ? 'active' : 'inactive'}`}>
                    {rule.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="mkt-auto-rule-actions">
                  <button
                    className="mkt-auto-btn-icon"
                    onClick={() => toggleRule(rule.id)}
                    title={rule.isActive ? 'Pause' : 'Activate'}
                  >
                    {rule.isActive ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <button
                    className="mkt-auto-btn-icon"
                    onClick={() => editRule(rule)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="mkt-auto-btn-icon danger"
                    onClick={() => setConfirmDeleteRuleId(rule.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {rule.description && (
                <p className="mkt-auto-rule-desc">{rule.description}</p>
              )}

              <div className="mkt-auto-workflow">
                <span className="mkt-auto-contact-type-badge">
                  {(() => { const ct = rule.targetContactType || rule.triggerConfig?.targetContactType || 'MERCHANT'; return ct === 'ALL' ? 'All' : ct.charAt(0) + ct.slice(1).toLowerCase(); })()}
                </span>
                <div className="mkt-auto-workflow-item trigger">
                  <Zap size={16} />
                  <span>{rule.triggerType.replace(/_/g, ' ')}</span>
                </div>
                <ArrowRight size={16} className="mkt-auto-workflow-arrow" />
                <div className="mkt-auto-workflow-item conditions">
                  <Filter size={16} />
                  <span>{Array.isArray(rule.conditions) ? rule.conditions.length : 0} conditions</span>
                </div>
                <ArrowRight size={16} className="mkt-auto-workflow-arrow" />
                <div className="mkt-auto-workflow-item actions">
                  <Settings size={16} />
                  <span>{Array.isArray(rule.actions) ? rule.actions.length : 0} actions</span>
                </div>
              </div>

              <div className="mkt-auto-rule-stats">
                <div className="mkt-auto-stat-item">
                  <strong>{rule.totalExecutions || 0}</strong>
                  <span>Executions</span>
                </div>
                <div className="mkt-auto-stat-item">
                  <strong>{rule.successfulExecutions || 0}</strong>
                  <span>Successful</span>
                </div>
                <div className="mkt-auto-stat-item">
                  <strong>Priority {rule.priority}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Templates View
  const TemplatesView = () => {
    const templates = [
      {
        id: 'welcome-new-merchant',
        name: 'Welcome New Merchants',
        description: 'Send a welcome email when a new merchant signs up',
        icon: '👋',
        triggerType: 'MERCHANT_SIGNUP',
        conditions: [],
        actions: [
          {
            type: 'SEND_EMAIL',
            config: {
              subject: 'Welcome to our platform, {{merchantName}}!',
              body: 'Hi {{merchantName}},\n\nWelcome to our platform! We\'re excited to have you on board.\n\nGet started by setting up your store and adding your first products.\n\nBest regards,\nThe Team'
            }
          },
          {
            type: 'ADD_TAG',
            config: { tagName: 'new-merchant' }
          }
        ]
      },
      {
        id: 'tier-upgrade-celebration',
        name: 'Celebrate Tier Upgrades',
        description: 'Congratulate merchants when they upgrade their subscription tier',
        icon: '🎉',
        triggerType: 'TIER_UPGRADED',
        conditions: [],
        actions: [
          {
            type: 'SEND_EMAIL',
            config: {
              subject: 'Congratulations on upgrading, {{merchantName}}!',
              body: 'Hi {{merchantName}},\n\nCongratulations on upgrading your subscription! You now have access to premium features.\n\nExplore your new capabilities in the dashboard.\n\nThank you for growing with us!'
            }
          },
          {
            type: 'CREATE_NOTIFICATION',
            config: {
              title: 'Tier Upgraded',
              message: 'Congratulations! You\'ve upgraded to a new tier.'
            }
          }
        ]
      },
      {
        id: 'first-order-milestone',
        name: 'First Order Celebration',
        description: 'Celebrate when a merchant receives their first order',
        icon: '🎊',
        triggerType: 'FIRST_ORDER',
        conditions: [],
        actions: [
          {
            type: 'SEND_EMAIL',
            config: {
              subject: 'Congratulations on your first order! 🎉',
              body: 'Hi {{merchantName}},\n\nCongratulations! You just received your first order!\n\nThis is an exciting milestone. Keep up the great work!\n\nCheers,\nThe Team'
            }
          },
          {
            type: 'ADD_TAG',
            config: { tagName: 'first-order-complete' }
          }
        ]
      },
      {
        id: 'inactive-merchant-reengagement',
        name: 'Re-engage Inactive Merchants',
        description: 'Reach out to merchants who haven\'t been active recently',
        icon: '📬',
        triggerType: 'INACTIVITY',
        conditions: [
          { field: 'DAYS_SINCE_LAST_ORDER', operator: 'greater_than', value: '30' }
        ],
        actions: [
          {
            type: 'SEND_EMAIL',
            config: {
              subject: 'We miss you, {{merchantName}}!',
              body: 'Hi {{merchantName}},\n\nWe noticed you haven\'t been active recently. Is there anything we can help with?\n\nWe\'d love to see you back and help you grow your business.\n\nFeel free to reach out if you need any assistance!'
            }
          },
          {
            type: 'ADD_TAG',
            config: { tagName: 'needs-reengagement' }
          }
        ]
      },
      {
        id: 'subscription-expiry-reminder',
        name: 'Subscription Expiry Reminder',
        description: 'Remind merchants before their subscription expires',
        icon: '⏰',
        triggerType: 'SUBSCRIPTION_EXPIRED',
        conditions: [],
        actions: [
          {
            type: 'SEND_EMAIL',
            config: {
              subject: 'Your subscription is expiring soon',
              body: 'Hi {{merchantName}},\n\nYour subscription is about to expire. Don\'t lose access to your premium features!\n\nRenew now to continue enjoying all the benefits.\n\nThank you for being a valued member!'
            }
          },
          {
            type: 'CREATE_NOTIFICATION',
            config: {
              title: 'Subscription Expiring',
              message: 'Your subscription is expiring soon. Renew to maintain access.'
            }
          }
        ]
      },
      {
        id: 'order-milestone-100',
        name: '100 Orders Milestone',
        description: 'Celebrate when a merchant reaches 100 orders',
        icon: '🏆',
        triggerType: 'ORDER_MILESTONE',
        conditions: [
          { field: 'TOTAL_ORDERS', operator: 'equals', value: '100' }
        ],
        actions: [
          {
            type: 'SEND_EMAIL',
            config: {
              subject: 'You\'ve reached 100 orders! 🏆',
              body: 'Hi {{merchantName}},\n\nAmazing! You\'ve just reached 100 orders!\n\nThis is a significant milestone. We\'re proud of your success and look forward to celebrating many more milestones with you.\n\nKeep up the fantastic work!'
            }
          },
          {
            type: 'ADD_TAG',
            config: { tagName: '100-orders-milestone' }
          }
        ]
      }
    ];

    const applyTemplate = (template) => {
      setSelectedRule({
        name: template.name,
        description: template.description,
        triggerType: template.triggerType,
        triggerConfig: {},
        conditions: template.conditions,
        conditionsLogic: 'AND',
        actions: template.actions,
        isActive: true,
        priority: 0
      });
      setShowBuilder(true);
    };

    return (
      <div className="mkt-auto-overview">
        <div className="mkt-auto-header">
          <div>
            <h1>Automation Templates</h1>
            <p>Pre-built automation workflows to get started quickly</p>
          </div>
        </div>

        <div className="mkt-auto-rules-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
          {templates.map((template) => (
            <div key={template.id} className="mkt-auto-rule-card" style={{ cursor: 'pointer' }} onClick={() => applyTemplate(template)}>
              <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '16px' }}>
                {template.icon}
              </div>
              <h3 style={{ fontSize: '18px', marginBottom: '8px', textAlign: 'center' }}>
                {template.name}
              </h3>
              <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '16px' }}>
                {template.description}
              </p>

              <div className="mkt-auto-workflow">
                <div className="mkt-auto-workflow-item trigger">
                  <Zap size={14} />
                  <span>{template.triggerType.replace(/_/g, ' ')}</span>
                </div>
                <ArrowRight size={14} className="mkt-auto-workflow-arrow" />
                <div className="mkt-auto-workflow-item conditions">
                  <Filter size={14} />
                  <span>{template.conditions.length} conditions</span>
                </div>
                <ArrowRight size={14} className="mkt-auto-workflow-arrow" />
                <div className="mkt-auto-workflow-item actions">
                  <Settings size={14} />
                  <span>{template.actions.length} actions</span>
                </div>
              </div>

              <button
                className="mkt-auto-btn-primary"
                style={{ width: '100%', marginTop: '16px', justifyContent: 'center' }}
                onClick={(e) => {
                  e.stopPropagation();
                  applyTemplate(template);
                }}
              >
                <Copy size={16} />
                Use Template
              </button>
            </div>
          ))}
        </div>

        <div className="mkt-auto-info-text" style={{ marginTop: '32px', textAlign: 'center' }}>
          Click on any template to customize it or use the "Use Template" button to apply it directly
        </div>
      </div>
    );
  };

  // Workflow Builder with Condition and Action Builders
  const WorkflowBuilder = () => {
    const [ruleData, setRuleData] = useState(() => {
      if (selectedRule) {
        return {
          ...selectedRule,
          targetContactType: selectedRule.targetContactType || selectedRule.triggerConfig?.targetContactType || 'MERCHANT'
        };
      }
      return {
        name: '',
        description: '',
        targetContactType: 'MERCHANT',
        triggerType: '',
        triggerConfig: {},
        conditions: [],
        conditionsLogic: 'AND',
        actions: [],
        isActive: true,
        priority: 0
      };
    });
    const [saving, setSaving] = useState(false);

    const addCondition = () => {
      setRuleData({
        ...ruleData,
        conditions: [...ruleData.conditions, { field: '', operator: 'equals', value: '' }]
      });
    };

    const updateCondition = (index, key, value) => {
      const updatedConditions = [...ruleData.conditions];
      updatedConditions[index] = { ...updatedConditions[index], [key]: value };
      setRuleData({ ...ruleData, conditions: updatedConditions });
    };

    const removeCondition = (index) => {
      const updatedConditions = ruleData.conditions.filter((_, i) => i !== index);
      setRuleData({ ...ruleData, conditions: updatedConditions });
    };

    const addAction = () => {
      setRuleData({
        ...ruleData,
        actions: [...ruleData.actions, { type: '', config: {} }]
      });
    };

    const updateAction = (index, key, value) => {
      const updatedActions = [...ruleData.actions];
      if (key === 'type') {
        updatedActions[index] = { type: value, config: {} };
      } else {
        updatedActions[index] = {
          ...updatedActions[index],
          config: { ...updatedActions[index].config, [key]: value }
        };
      }
      setRuleData({ ...ruleData, actions: updatedActions });
    };

    const removeAction = (index) => {
      const updatedActions = ruleData.actions.filter((_, i) => i !== index);
      setRuleData({ ...ruleData, actions: updatedActions });
    };

    const getOperatorsForCondition = (fieldType) => {
      const baseOperators = [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' }
      ];

      if (['TOTAL_ORDERS', 'TOTAL_REVENUE', 'DAYS_SINCE_SIGNUP', 'DAYS_SINCE_LAST_ORDER', 'ACCOUNT_AGE_DAYS', 'PRODUCT_COUNT', 'LAST_LOGIN_DAYS', 'ORDER_COUNT'].includes(fieldType)) {
        return [
          ...baseOperators,
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'greater_or_equal', label: 'Greater or Equal' },
          { value: 'less_or_equal', label: 'Less or Equal' }
        ];
      }

      if (['HAS_TAG', 'IN_SEGMENT'].includes(fieldType)) {
        return [
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does Not Contain' }
        ];
      }

      if (['EMAIL_VERIFIED', 'ONBOARDING_COMPLETED', 'STORE_ACTIVE', 'STORE_PUBLISHED', 'HAS_ORDERS'].includes(fieldType)) {
        return [
          { value: 'equals', label: 'Is' }
        ];
      }

      return baseOperators;
    };

    const saveRule = async () => {
      if (saving) return;
      setSaving(true);
      try {
        // Validate required fields
        if (!ruleData.name || !ruleData.name.trim()) {
          showStatus('error', 'Please enter a rule name');
          setSaving(false);
          return;
        }
        if (!ruleData.triggerType) {
          showStatus('error', 'Please select a trigger type');
          setSaving(false);
          return;
        }
        if (ruleData.actions.length === 0) {
          showStatus('error', 'Please add at least one action');
          setSaving(false);
          return;
        }

        // Only update if ruleData has a valid UUID (database rules have UUIDs, templates have string IDs)
        const hasValidDatabaseId = ruleData.id && ruleData.id.length > 30; // UUIDs are ~36 chars
        const isUpdate = hasValidDatabaseId;

        const url = isUpdate
          ? `/admin/automation/rules/${ruleData.id}`
          : '/admin/automation/rules';
        const method = isUpdate ? 'PUT' : 'POST';

        const response = await apiWithAccessControl(url, {
          method,
          body: JSON.stringify(ruleData)
        });

        const data = await response.json();
        if (data.success) {
          showStatus('success', data.message || 'Rule saved successfully');
          setShowBuilder(false);
          loadRules();
        } else {
          showStatus('error', data.message || 'Failed to save rule');
        }
      } catch (error) {
        console.error('Error saving rule:', error);
        showStatus('error', 'Failed to save rule');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="mkt-auto-builder">
        <div className="mkt-auto-builder-header">
          <h2>{selectedRule ? 'Edit Rule' : 'Create New Rule'}</h2>
          <button className="mkt-auto-btn-icon" onClick={() => setShowBuilder(false)}>×</button>
        </div>

        <div className="mkt-auto-builder-body">
          {/* Basic Info */}
          <div className="mkt-auto-builder-section">
            <h3>Basic Information</h3>
            <div className="mkt-auto-form-group">
              <label>Rule Name *</label>
              <input
                type="text"
                value={ruleData.name}
                onChange={(e) => setRuleData({ ...ruleData, name: e.target.value })}
                placeholder="e.g., Welcome New Merchants"
              />
            </div>
            <div className="mkt-auto-form-group">
              <label>Description</label>
              <textarea
                value={ruleData.description || ''}
                onChange={(e) => setRuleData({ ...ruleData, description: e.target.value })}
                placeholder="Describe what this rule does..."
                rows={3}
              />
            </div>
          </div>

          {/* Target Contact Type */}
          <div className="mkt-auto-builder-section">
            <h3>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              {' '}Applies To
            </h3>
            <p className="mkt-auto-section-desc">Which contact type should this rule target?</p>
            <div className="mkt-auto-form-group">
              <label>Contact Type *</label>
              <select
                value={ruleData.targetContactType || 'MERCHANT'}
                onChange={(e) => setRuleData({ ...ruleData, targetContactType: e.target.value })}
                className="mkt-auto-contact-type-select"
              >
                {(options.contactTypes || [
                  { value: 'ALL', label: 'All Contacts' },
                  { value: 'MERCHANT', label: 'Merchants' },
                  { value: 'CUSTOMER', label: 'Customers' },
                  { value: 'LEAD', label: 'Leads' },
                  { value: 'WAITLIST', label: 'Waitlist Subscribers' }
                ]).map((ct) => (
                  <option key={ct.value} value={ct.value}>{ct.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Trigger */}
          <div className="mkt-auto-builder-section">
            <h3><Zap size={20} /> Trigger</h3>
            <p className="mkt-auto-section-desc">When should this automation run?</p>
            <div className="mkt-auto-form-group">
              <label>Trigger Type *</label>
              <select
                value={ruleData.triggerType}
                onChange={(e) => setRuleData({ ...ruleData, triggerType: e.target.value })}
              >
                <option value="">Select trigger...</option>
                {options.triggers.map((trigger) => (
                  <option key={trigger.value} value={trigger.value}>
                    {trigger.label} - {trigger.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Trigger Configuration - Dynamic based on trigger type */}
            {(ruleData.triggerType === 'ORDER_MILESTONE' || ruleData.triggerType === 'REVENUE_MILESTONE') && (
              <div className="mkt-auto-form-group">
                <label>
                  {ruleData.triggerType === 'ORDER_MILESTONE' ? 'Number of Orders *' : 'Revenue Amount *'}
                </label>
                <input
                  type="number"
                  placeholder={ruleData.triggerType === 'ORDER_MILESTONE' ? 'e.g., 100' : 'e.g., 5000'}
                  value={ruleData.triggerConfig?.threshold || ''}
                  onChange={(e) => setRuleData({
                    ...ruleData,
                    triggerConfig: { threshold: ruleData.triggerType === 'ORDER_MILESTONE' ? parseInt(e.target.value) : parseFloat(e.target.value) }
                  })}
                />
                <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  {ruleData.triggerType === 'ORDER_MILESTONE'
                    ? 'Trigger when merchant reaches this number of orders'
                    : 'Trigger when merchant reaches this revenue amount'
                  }
                </small>
              </div>
            )}

            {(ruleData.triggerType === 'INACTIVITY' || ruleData.triggerType === 'NO_ORDERS') && (
              <div className="mkt-auto-form-group">
                <label>Number of Days *</label>
                <input
                  type="number"
                  placeholder="e.g., 30"
                  value={ruleData.triggerConfig?.days || ''}
                  onChange={(e) => setRuleData({
                    ...ruleData,
                    triggerConfig: { days: parseInt(e.target.value) }
                  })}
                />
                <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  {ruleData.triggerType === 'INACTIVITY'
                    ? 'Trigger when merchant has no activity for this many days'
                    : 'Trigger when merchant has no orders for this many days'
                  }
                </small>
              </div>
            )}

            {(ruleData.triggerType === 'TAG_ADDED' || ruleData.triggerType === 'TAG_REMOVED') && (
              <div className="mkt-auto-form-group">
                <label>Tag Name *</label>
                <select
                  value={ruleData.triggerConfig?.tagName || ''}
                  onChange={(e) => setRuleData({
                    ...ruleData,
                    triggerConfig: { tagName: e.target.value }
                  })}
                >
                  <option value="">Select a tag...</option>
                  {tagOptions.map(tag => (
                    <option key={tag.value} value={tag.value}>
                      {tag.label} ({tag.count} merchants)
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  Trigger when this specific tag is {ruleData.triggerType === 'TAG_ADDED' ? 'added to' : 'removed from'} a merchant
                </small>
              </div>
            )}

            {(ruleData.triggerType === 'SEGMENT_ENTERED' || ruleData.triggerType === 'SEGMENT_EXITED') && (
              <div className="mkt-auto-form-group">
                <label>Segment *</label>
                <select
                  value={ruleData.triggerConfig?.segmentId || ''}
                  onChange={(e) => setRuleData({
                    ...ruleData,
                    triggerConfig: { segmentId: e.target.value }
                  })}
                >
                  <option value="">Select a segment...</option>
                  {segmentOptions.map(seg => (
                    <option key={seg.value} value={seg.value}>
                      {seg.label} ({seg.type} — {seg.recipientCount} recipients)
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  Trigger when merchant {ruleData.triggerType === 'SEGMENT_ENTERED' ? 'enters' : 'exits'} this segment
                </small>
              </div>
            )}

            {ruleData.triggerType === 'CUSTOM_EVENT' && (
              <div className="mkt-auto-form-group">
                <label>Event Name *</label>
                <input
                  type="text"
                  placeholder="e.g., custom_milestone_reached"
                  value={ruleData.triggerConfig?.eventName || ''}
                  onChange={(e) => setRuleData({
                    ...ruleData,
                    triggerConfig: { eventName: e.target.value }
                  })}
                />
                <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  Custom event name to listen for
                </small>
              </div>
            )}

            {ruleData.triggerType === 'SCHEDULED' && (
              <div className="mkt-auto-form-group">
                <label>Schedule (Cron Expression) *</label>
                <input
                  type="text"
                  placeholder="e.g., 0 9 * * * (Daily at 9am)"
                  value={ruleData.triggerConfig?.schedule || ''}
                  onChange={(e) => setRuleData({
                    ...ruleData,
                    triggerConfig: { schedule: e.target.value }
                  })}
                />
                <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  Cron expression for scheduled execution (e.g., "0 9 * * *" for daily at 9am)
                </small>
              </div>
            )}
          </div>

          {/* Conditions */}
          <div className="mkt-auto-builder-section">
            <h3><Filter size={20} /> Conditions (Optional)</h3>
            <p className="mkt-auto-section-desc">Additional criteria for this rule</p>
            <div className="mkt-auto-form-group">
              <label>Logic</label>
              <select
                value={ruleData.conditionsLogic}
                onChange={(e) => setRuleData({ ...ruleData, conditionsLogic: e.target.value })}
              >
                <option value="AND">Match ALL conditions (AND)</option>
                <option value="OR">Match ANY condition (OR)</option>
              </select>
            </div>

            {/* Condition List */}
            {ruleData.conditions.length > 0 && (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {ruleData.conditions.map((condition, index) => {
                  const selectedCondition = options.conditions.find(c => c.value === condition.field);
                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr auto',
                      gap: '12px',
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '6px'
                    }}>
                      {/* Condition Field */}
                      <select
                        value={condition.field}
                        onChange={(e) => updateCondition(index, 'field', e.target.value)}
                        style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                      >
                        <option value="">Select field...</option>
                        {options.conditions.map((cond) => (
                          <option key={cond.value} value={cond.value}>
                            {cond.label}
                          </option>
                        ))}
                      </select>

                      {/* Operator */}
                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                        style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                      >
                        {getOperatorsForCondition(condition.field).map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>

                      {/* Value */}
                      {selectedCondition && selectedCondition.type === 'select' ? (
                        <select
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                        >
                          <option value="">Select value...</option>
                          {selectedCondition.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : selectedCondition && selectedCondition.type === 'boolean' ? (
                        <select
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                        >
                          <option value="">Select...</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : selectedCondition && selectedCondition.type === 'tag-select' ? (
                        <select
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                        >
                          <option value="">Select tag...</option>
                          {tagOptions.map((tag) => (
                            <option key={tag.value || tag} value={tag.value || tag}>
                              {tag.label || tag}
                            </option>
                          ))}
                        </select>
                      ) : selectedCondition && selectedCondition.type === 'segment-select' ? (
                        <select
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                        >
                          <option value="">Select segment...</option>
                          {segmentOptions.map((seg) => (
                            <option key={seg.value || seg.id} value={seg.value || seg.name}>
                              {seg.label || seg.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={selectedCondition && selectedCondition.type === 'number' ? 'number' : 'text'}
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          placeholder="Enter value..."
                          style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                        />
                      )}

                      {/* Remove Button */}
                      <button
                        className="mkt-auto-btn-icon danger"
                        onClick={() => removeCondition(index)}
                        title="Remove condition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Condition Button */}
            <button
              className="mkt-auto-btn-secondary"
              onClick={addCondition}
              style={{ marginTop: '16px' }}
            >
              <Plus size={16} />
              Add Condition
            </button>
          </div>

          {/* Actions */}
          <div className="mkt-auto-builder-section">
            <h3><Settings size={20} /> Actions</h3>
            <p className="mkt-auto-section-desc">What should happen when triggered?</p>

            {/* Action List */}
            {ruleData.actions.length > 0 && (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {ruleData.actions.map((action, index) => {
                  const selectedAction = options.actions.find(a => a.value === action.type);
                  return (
                    <div key={index} style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <strong style={{ color: '#155a51' }}>Action {index + 1}</strong>
                        <button
                          className="mkt-auto-btn-icon danger"
                          onClick={() => removeAction(index)}
                          title="Remove action"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Action Type */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                          Action Type
                        </label>
                        <select
                          value={action.type}
                          onChange={(e) => updateAction(index, 'type', e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                        >
                          <option value="">Select action...</option>
                          {options.actions.map((act) => (
                            <option key={act.value} value={act.value}>
                              {act.label} - {act.description}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Action Configuration Based on Type */}
                      {action.type === 'SEND_EMAIL' && (
                        <>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                              Email Subject
                            </label>
                            <input
                              type="text"
                              value={action.config.subject || ''}
                              onChange={(e) => updateAction(index, 'subject', e.target.value)}
                              placeholder="Email subject..."
                              style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                              Email Body
                            </label>
                            <textarea
                              value={action.config.body || ''}
                              onChange={(e) => updateAction(index, 'body', e.target.value)}
                              placeholder="Email content... Use {{merchantName}}, {{merchantEmail}} for placeholders"
                              rows={4}
                              style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            />
                          </div>
                        </>
                      )}

                      {action.type === 'SEND_SMS' && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                            SMS Message
                          </label>
                          <textarea
                            value={action.config.message || ''}
                            onChange={(e) => updateAction(index, 'message', e.target.value)}
                            placeholder="SMS message... Use {{merchantName}} for placeholders"
                            rows={3}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                          />
                        </div>
                      )}

                      {action.type === 'ADD_TAG' && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                            Tag Name
                          </label>
                          <select
                            value={action.config.tagName || ''}
                            onChange={(e) => updateAction(index, 'tagName', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                          >
                            <option value="">Select a tag...</option>
                            {tagOptions.map(tag => (
                              <option key={tag.value} value={tag.value}>
                                {tag.label} ({tag.count} merchants)
                              </option>
                            ))}
                          </select>
                          <label style={{ display: 'block', marginBottom: '4px', marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                            Or type a new tag name:
                          </label>
                          <input
                            type="text"
                            value={action.config.tagName || ''}
                            onChange={(e) => updateAction(index, 'tagName', e.target.value)}
                            placeholder="Or enter a new tag name..."
                            style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px', marginTop: '4px' }}
                          />
                        </div>
                      )}

                      {action.type === 'REMOVE_TAG' && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                            Tag Name
                          </label>
                          <select
                            value={action.config.tagName || ''}
                            onChange={(e) => updateAction(index, 'tagName', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                          >
                            <option value="">Select a tag to remove...</option>
                            {tagOptions.map(tag => (
                              <option key={tag.value} value={tag.value}>
                                {tag.label} ({tag.count} merchants)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {(action.type === 'ADD_TO_SEGMENT' || action.type === 'REMOVE_FROM_SEGMENT') && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                            {action.type === 'ADD_TO_SEGMENT' ? 'Add to Segment' : 'Remove from Segment'}
                          </label>
                          <select
                            value={action.config.segmentId || action.config.segmentName || ''}
                            onChange={(e) => {
                              const selected = segmentOptions.find(s => s.value === e.target.value);
                              updateAction(index, 'segmentId', e.target.value);
                              if (selected) {
                                updateAction(index, 'segmentName', selected.label);
                              }
                            }}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                          >
                            <option value="">Select a segment...</option>
                            {segmentOptions.map(seg => (
                              <option key={seg.value} value={seg.value}>
                                {seg.label} ({seg.type} — {seg.recipientCount} recipients)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {action.type === 'WAIT' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                              Duration
                            </label>
                            <input
                              type="number"
                              value={action.config.duration || ''}
                              onChange={(e) => updateAction(index, 'duration', e.target.value)}
                              placeholder="Enter duration..."
                              style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                              Unit
                            </label>
                            <select
                              value={action.config.unit || 'hours'}
                              onChange={(e) => updateAction(index, 'unit', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            >
                              <option value="minutes">Minutes</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {action.type === 'CREATE_NOTIFICATION' && (
                        <>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                              Notification Title
                            </label>
                            <input
                              type="text"
                              value={action.config.title || ''}
                              onChange={(e) => updateAction(index, 'title', e.target.value)}
                              placeholder="Notification title..."
                              style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                              Notification Message
                            </label>
                            <textarea
                              value={action.config.message || ''}
                              onChange={(e) => updateAction(index, 'message', e.target.value)}
                              placeholder="Notification message..."
                              rows={3}
                              style={{ width: '100%', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            />
                          </div>
                        </>
                      )}

                      {selectedAction && !['SEND_EMAIL', 'SEND_SMS', 'ADD_TAG', 'REMOVE_TAG', 'ADD_TO_SEGMENT', 'REMOVE_FROM_SEGMENT', 'WAIT', 'CREATE_NOTIFICATION'].includes(action.type) && (
                        <div className="mkt-auto-info-text" style={{ margin: 0 }}>
                          Configuration for {selectedAction.label} can be added as needed
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Action Button */}
            <button
              className="mkt-auto-btn-secondary"
              onClick={addAction}
              style={{ marginTop: '16px' }}
            >
              <Plus size={16} />
              Add Action
            </button>
          </div>

          {/* Settings */}
          <div className="mkt-auto-builder-section">
            <h3><Settings size={20} /> Settings</h3>
            <div className="mkt-auto-form-row">
              <div className="mkt-auto-form-group">
                <label>Priority</label>
                <input
                  type="number"
                  value={ruleData.priority}
                  onChange={(e) => setRuleData({ ...ruleData, priority: parseInt(e.target.value) })}
                  min="0"
                />
                <small>Higher priority rules execute first</small>
              </div>
              <div className="mkt-auto-form-group">
                <label className="mkt-auto-checkbox">
                  <input
                    type="checkbox"
                    checked={ruleData.isActive}
                    onChange={(e) => setRuleData({ ...ruleData, isActive: e.target.checked })}
                  />
                  <span>Activate rule immediately</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="mkt-auto-builder-footer">
          <button className="mkt-auto-btn-secondary" onClick={() => setShowBuilder(false)}>
            Cancel
          </button>
          <button className="mkt-auto-btn-primary" onClick={saveRule} disabled={saving}>
            {saving ? 'Saving...' : (selectedRule ? 'Update Rule' : 'Create Rule')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mkt-auto-dashboard">
      {/* Status Banner */}
      {statusMessage && (
        <div className={`mkt-auto-status-banner mkt-auto-status-${statusMessage.type}`}>
          {statusMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span>{statusMessage.text}</span>
          <button className="mkt-auto-status-close" onClick={() => setStatusMessage(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mkt-auto-tabs">
        <button
          className={`mkt-auto-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={18} />
          Overview
        </button>
        <button
          className={`mkt-auto-tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          <GitBranch size={18} />
          Rules
        </button>
        <button
          className={`mkt-auto-tab ${activeTab === 'executions' ? 'active' : ''}`}
          onClick={() => setActiveTab('executions')}
        >
          <Activity size={18} />
          Executions
        </button>
        <button
          className={`mkt-auto-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          <Copy size={18} />
          Templates
        </button>
      </div>

      {/* Content */}
      <div className="mkt-auto-content">
        {activeTab === 'overview' && <OverviewDashboard />}
        {activeTab === 'rules' && <RulesListView />}
        {activeTab === 'executions' && <ExecutionHistoryView />}
        {activeTab === 'templates' && <TemplatesView />}
      </div>

      {/* Workflow Builder Modal */}
      {showBuilder && (
        <div className="mkt-auto-modal-overlay">
          <div className="mkt-auto-modal large">
            <WorkflowBuilder />
          </div>
        </div>
      )}

      {/* Execution Details Modal */}
      {selectedExecution && (
        <div className="mkt-auto-modal-overlay" onClick={() => setSelectedExecution(null)}>
          <div className="mkt-auto-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mkt-auto-builder-header">
              <h2>Execution Details</h2>
              <button className="mkt-auto-btn-icon" onClick={() => setSelectedExecution(null)}>×</button>
            </div>

            <div className="mkt-auto-builder-body">
              {/* Basic Info */}
              <div className="mkt-auto-builder-section">
                <h3>Execution Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Rule Name</label>
                    <p style={{ margin: 0 }}>{selectedExecution.rule?.name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Status</label>
                    <span className={`mkt-auto-status-badge ${selectedExecution.status === 'SUCCESS' ? 'active' : 'inactive'}`}>
                      {selectedExecution.status}
                    </span>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Merchant ID</label>
                    <p style={{ margin: 0 }}>{selectedExecution.merchantId}</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Executed At</label>
                    <p style={{ margin: 0 }}>{new Date(selectedExecution.executedAt).toLocaleString()}</p>
                  </div>
                  {selectedExecution.completedAt && (
                    <div>
                      <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Completed At</label>
                      <p style={{ margin: 0 }}>{new Date(selectedExecution.completedAt).toLocaleString()}</p>
                    </div>
                  )}
                  {selectedExecution.executionDuration && (
                    <div>
                      <label style={{ display: 'block', fontWeight: '500', marginBottom: '4px' }}>Duration</label>
                      <p style={{ margin: 0 }}>
                        {selectedExecution.executionDuration < 1000
                          ? `${selectedExecution.executionDuration}ms`
                          : `${(selectedExecution.executionDuration / 1000).toFixed(2)}s`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Trigger Data */}
              {selectedExecution.triggerData && Object.keys(selectedExecution.triggerData).length > 0 && (
                <div className="mkt-auto-builder-section">
                  <h3>Trigger Data</h3>
                  <pre style={{
                    background: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(selectedExecution.triggerData, null, 2)}
                  </pre>
                </div>
              )}

              {/* Conditions Result */}
              {selectedExecution.conditionsResult && (
                <div className="mkt-auto-builder-section">
                  <h3>Conditions Result</h3>
                  <pre style={{
                    background: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(selectedExecution.conditionsResult, null, 2)}
                  </pre>
                </div>
              )}

              {/* Actions Result */}
              {selectedExecution.actionsResult && (
                <div className="mkt-auto-builder-section">
                  <h3>Actions Result</h3>
                  <pre style={{
                    background: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(selectedExecution.actionsResult, null, 2)}
                  </pre>
                </div>
              )}

              {/* Error Message */}
              {selectedExecution.errorMessage && (
                <div className="mkt-auto-builder-section">
                  <h3>Error Details</h3>
                  <div style={{
                    background: '#fee2e2',
                    color: '#991b1b',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    {selectedExecution.errorMessage}
                  </div>
                </div>
              )}
            </div>

            <div className="mkt-auto-builder-footer">
              <button className="mkt-auto-btn-secondary" onClick={() => setSelectedExecution(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDeleteRuleId && (
        <div className="mkt-auto-modal-overlay" onClick={() => setConfirmDeleteRuleId(null)}>
          <div className="mkt-auto-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="mkt-auto-confirm-icon">
              <Trash2 size={24} />
            </div>
            <h3>Delete Automation Rule</h3>
            <p>Are you sure you want to delete the rule "{rules.find(r => r.id === confirmDeleteRuleId)?.name || 'this rule'}"? This action cannot be undone.</p>
            <div className="mkt-auto-confirm-actions">
              <button
                className="mkt-auto-btn-secondary"
                onClick={() => setConfirmDeleteRuleId(null)}
                disabled={deletingRuleId}
              >
                Cancel
              </button>
              <button
                className="mkt-auto-btn-danger"
                onClick={() => deleteRule(confirmDeleteRuleId)}
                disabled={deletingRuleId}
              >
                {deletingRuleId ? 'Deleting...' : 'Delete Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AutomationDashboard;
